import { SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";

import CampaignJobBoardCard from "./CampaignJobBoardCard";
import CampaignJobBoardCardSkeleton from "./CampaignJobBoardCardSkeleton";

import Icons from "@/components/icons";
import EmptyState from "@/components/ui/v2/empty-state";
import { getAddCampaignIntegrationPath } from "@/features/campaigns/utils/navigation";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";

interface CampaignJobBoardListProps {
  jobBoards: CampaignJobBoardType[];
  campaignId: string;
  clientId: string;
  canEdit: boolean;
  canAdd: boolean;
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

function CampaignJobBoardList({
  jobBoards,
  campaignId,
  clientId,
  canAdd,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: CampaignJobBoardListProps) {
  const { t } = useTranslation();
  const { clientId: paramClientId, campaignId: paramCampaignId } = useParams();

  const resolvedClientId = clientId || paramClientId || "";
  const resolvedCampaignId = campaignId || paramCampaignId || "";

  if (isLoading) {
    return (
      <div className="rounded-md border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  if (jobBoards.length === 0) {
    return (
      <EmptyState
        icon={SearchX}
        title={t("campaigns.jobBoards.noData.title", "No job boards found")}
        description={
          canAdd
            ? t(
              "campaigns.jobBoards.noData.description",
              "Add job boards to this campaign to get started",
            )
            : undefined
        }
        action={
          canAdd
            ? {
              label: t("actions.add", { entity: t("common.jobBoard") }),
              href: getAddCampaignIntegrationPath(
                resolvedClientId,
                resolvedCampaignId,
              ),
              icon: <Icons.CrossIcon className="size-4 mr-2 rotate-45" />,
            }
            : undefined
        }
      />
    );
  }

  // Group job boards by status
  const activeJobBoards = jobBoards.filter((jobBoard) => jobBoard.isActive);
  const inactiveJobBoards = jobBoards.filter((jobBoard) => !jobBoard.isActive);

  const allJobBoards = [...activeJobBoards, ...inactiveJobBoards];

  // Show skeleton at bottom when:
  // 1. Loading more pages (hasNextPage && isFetchingNextPage)
  // 2. Loading state (isLoading) - show regardless of hasNextPage
  const showBottomSkeleton =
    (hasNextPage && isFetchingNextPage) || (isLoading && jobBoards.length > 0);

  return (
    <InfiniteScroll
      dataLength={allJobBoards.length}
      next={fetchNextPage || (() => {})}
      hasMore={hasNextPage || false}
      loader={
        showBottomSkeleton ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <CampaignJobBoardCardSkeleton key={i} />
            ))}
          </div>
        ) : null
      }
      scrollThreshold={0.9}
      scrollableTarget="main-content-container"
    >
      <div className="space-y-8">
        {activeJobBoards.length > 0 && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {activeJobBoards.map((jobBoard) => (
                <CampaignJobBoardCard
                  key={jobBoard.clientIntegrationModuleId}
                  jobBoard={jobBoard}
                  campaignId={campaignId}
                  clientId={clientId}
                />
              ))}
            </div>
          </div>
        )}

        {inactiveJobBoards.length > 0 && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {inactiveJobBoards.map((jobBoard) => (
                <CampaignJobBoardCard
                  key={jobBoard.clientIntegrationModuleId}
                  jobBoard={jobBoard}
                  campaignId={campaignId}
                  clientId={clientId}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </InfiniteScroll>
  );
}

export default CampaignJobBoardList;
